import { useCallback, useState } from "react";
import type { BloomThemeScore } from "../types/bloom-content";
import { type ThemeGardenSlot } from "../lib/results-garden";
import { isBloomDev } from "../lib/debug";
import { ResultsDebugPanel } from "./ResultsDebugPanel";
import { ResultsGarden } from "./ResultsGarden";
import { ResultsOverview } from "./ResultsOverview";
import { ThemeDetailModal } from "./ThemeDetailModal";

interface ResultsSummaryProps {
  scores: BloomThemeScore[];
  onRestart: () => void;
}

function initialTierOverrides(_scores: BloomThemeScore[]): Record<string, number> {
  return {};
}

export function ResultsSummary({ scores, onRestart }: ResultsSummaryProps) {
  const [selectedThemeId, setSelectedThemeId] = useState<string | null>(null);
  const [tierOverrides, setTierOverrides] = useState(() => initialTierOverrides(scores));
  const [slotOverrides, setSlotOverrides] = useState<Record<string, Partial<ThemeGardenSlot>>>({});
  const [highlightThemeId, setHighlightThemeId] = useState<string | null>(null);
  const [showOverview, setShowOverview] = useState(false);
  const [printMode, setPrintMode] = useState(false);
  const selectedTheme = scores.find((s) => s.themeId === selectedThemeId) ?? null;

  const handleTierChange = useCallback((themeId: string, tier: number) => {
    setTierOverrides((prev) => ({ ...prev, [themeId]: tier }));
  }, []);

  const handleSlotChange = useCallback((themeId: string, patch: Partial<ThemeGardenSlot>) => {
    setSlotOverrides((prev) => ({ ...prev, [themeId]: { ...prev[themeId], ...patch } }));
  }, []);

  const handleSlotsReset = useCallback(() => setSlotOverrides({}), []);

  const handleSetAllTiers = useCallback(
    (mode: "auto" | number) => {
      setTierOverrides(() => {
        if (mode === "auto") return {};
        return Object.fromEntries(scores.map((s) => [s.themeId, mode]));
      });
    },
    [scores],
  );

  const handlePrint = useCallback(() => {
    setShowOverview(true);
    setPrintMode(true);
    const endPrint = () => {
      setPrintMode(false);
      window.removeEventListener("afterprint", endPrint);
    };
    window.addEventListener("afterprint", endPrint);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => window.print());
    });
  }, []);

  return (
    <div className={`bloom-results${printMode ? " bloom-print-active" : ""}`}>
      <header className="bloom-results__header">
        <h2 className="bloom-results__title">Jouw tuin</h2>
      </header>

      <ResultsGarden
        scores={scores}
        tierOverrides={isBloomDev() ? tierOverrides : undefined}
        slotOverrides={isBloomDev() ? slotOverrides : undefined}
        highlightThemeId={isBloomDev() ? highlightThemeId : undefined}
        onSelectTheme={setSelectedThemeId}
      />

      {isBloomDev() && (
        <ResultsDebugPanel
          scores={scores}
          tierOverrides={tierOverrides}
          onTierChange={handleTierChange}
          slotOverrides={slotOverrides}
          onSlotChange={handleSlotChange}
          onSlotsReset={handleSlotsReset}
          onSetAllTiers={handleSetAllTiers}
          onThemeHover={setHighlightThemeId}
        />
      )}

      {showOverview && <ResultsOverview scores={scores} onPrint={handlePrint} />}

      <div className="bloom-results__actions">
        <button
          type="button"
          className="bloom-btn bloom-btn--ghost bloom-results__toggle"
          aria-expanded={showOverview}
          onClick={() => setShowOverview((v) => !v)}
        >
          <span>{showOverview ? "Verberg resultaten" : "Bekijk resultaten"}</span>
          <svg
            className="bloom-results__toggle-chevron"
            viewBox="0 0 12 8"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M1 1l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <button
          type="button"
          className="bloom-btn bloom-btn--ghost bloom-btn--small"
          onClick={onRestart}
        >
          Opnieuw beginnen
        </button>
      </div>

      <ThemeDetailModal theme={selectedTheme} onClose={() => setSelectedThemeId(null)} />
    </div>
  );
}
